import { PagingParameters, RelativePosition } from "@/api/dtos";
import { FilterStatus } from "@/components/StatusTableTab";
import {
  FilterStatusContextType,
  useAdditionalFilters,
} from "@/context/additional-filters/AdditionalFiltersContext";
import { useAdvancedSearch } from "@/context/advanced-search/AdvancedSearchContext";
import { usePagination } from "@/context/pagination/PaginationProvider";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction, useEffect, useMemo, useRef } from "react";

export type FetchParams = {
  where: object;
  order: object[];
  take: number;
  skip: number;
};

type DashboardParams = {
  pagination: PagingParameters;
  setPagination: Dispatch<SetStateAction<PagingParameters>>;

  filterStatus: FilterStatus;
  setFilterStatus: Dispatch<SetStateAction<FilterStatus>>;

  fetchParams: FetchParams;

  countByStatus: any;
  currentCount: number;
  relativePosition?: RelativePosition;
};

export function useDashboardParams<T = unknown>({
  resourceName,
  statusField,
  resourceCounter,

  relativePosition,
}: {
  statusField?: keyof T;
  resourceName: string;
  resourceCounter?: (where: object) => Promise<any>;

  relativePosition?: {
    currentId: string;
    positionFetcher: (
      id: string,
      order: object,
      where: object,
    ) => Promise<RelativePosition>;
  };
}): DashboardParams {
  const { prismaWhereQueryContent, prismaOrderByQueryContent } =
    useAdvancedSearch();

  const { pagination, setPagination, setLastPosition } = usePagination();

  const {
    filterStatus,
    setWhereQuery,
    setFilterStatus,
    computeFilterStatusQueryContent,
  } = useAdditionalFilters() as FilterStatusContextType;

  const fetchParams: FetchParams = useMemo(
    () => ({
      where: {
        ...prismaWhereQueryContent,
        ...(statusField !== undefined
          ? computeFilterStatusQueryContent(statusField)
          : {}),
        ...setWhereQuery,
      },
      order: prismaOrderByQueryContent,
      take: pagination.pageSize,
      skip: pagination.page * pagination.pageSize,
    }),
    [
      setWhereQuery,
      pagination.page,
      pagination.pageSize,
      statusField,
      computeFilterStatusQueryContent,
      prismaOrderByQueryContent,
      prismaWhereQueryContent,
    ],
  );

  const dashboardQueryKey = useMemo(
    () =>
      JSON.stringify({
        filterStatus,
        prismaWhereQueryContent,
        prismaOrderByQueryContent,
        setWhereQuery,
      }),
    [
      filterStatus,
      prismaWhereQueryContent,
      prismaOrderByQueryContent,
      setWhereQuery,
    ],
  );

  const { data: fetchedRelativePosition } = useQuery({
    queryFn: () =>
      relativePosition?.positionFetcher(
        relativePosition?.currentId,
        fetchParams.order,
        fetchParams.where,
      ),
    queryKey: [
      "positionFetcher",
      resourceName,
      relativePosition?.currentId,
      dashboardQueryKey,
    ],
    placeholderData: keepPreviousData,
    enabled: relativePosition != undefined,
  });

  useEffect(() => {
    // when relative position is undefined, that means we are in the dashboard,
    // so we want to reset the "lastPosition"
    if (relativePosition == undefined) setLastPosition(undefined);
    // when we fetched the relative position, we want to update the pagination
    if (fetchedRelativePosition != undefined)
      setLastPosition(fetchedRelativePosition.position);
  }, [relativePosition, setLastPosition, fetchedRelativePosition]);

  const countParams = {
    ...prismaWhereQueryContent,
    ...setWhereQuery,
  };

  const { data: countByStatus } = useQuery({
    queryKey: ["countByStatus", resourceName, countParams],
    queryFn: () => resourceCounter?.(countParams),
    enabled: resourceCounter != undefined && resourceName != undefined,
  });

  function computeCurrentCount() {
    switch (filterStatus) {
      case "all":
        return (
          ((countByStatus?.published as number) ?? 0) +
          ((countByStatus?.draft as number) ?? 0)
        );
      case "published":
        return (countByStatus?.published as number) ?? 0;
      case "draft":
        return (countByStatus?.draft as number) ?? 0;
    }
  }

  const lastDashboardQueryKeyRef = useRef<string | undefined>(undefined);

  // Reset pagination when the effective query changes (filters/search/sort),
  // but NOT when the user changes pages.
  useEffect(() => {
    // Only auto-reset in dashboard listing mode (not in relative-position mode)
    if (relativePosition != undefined) return;

    const lastKey = lastDashboardQueryKeyRef.current;
    lastDashboardQueryKeyRef.current = dashboardQueryKey;

    // First render: initialize ref, don't reset
    if (lastKey === undefined) return;

    // No query change => do nothing (allows normal page navigation)
    if (lastKey === dashboardQueryKey) return;

    // Query changed => jump back to page 1
    if (pagination.page === 0) return;

    setPagination((prev) => ({ ...prev, page: 0 }));
  }, [setPagination, relativePosition, dashboardQueryKey, pagination.page]);

  return {
    pagination,
    setPagination,
    filterStatus,
    setFilterStatus,
    fetchParams,
    countByStatus,
    currentCount: computeCurrentCount(),
    relativePosition: fetchedRelativePosition,
  };
}
